import { useState, useEffect } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import Api from '../api/api';
import Enums from '../../utilities/enum';
import stringHelpers from '../../utilities/stringHelpers';
import arrayUtilities from '../../utilities/arrayUtilities';
import formUtilities from '../../utilities/formUtilities';
import { userRoleDescriptions } from '../../utilities/constants';
import { ErrorModal } from '../../utilities/errorModal';
import {toDateTimeString} from '../../utilities/dateUtilities'
import { ClientSelection } from './clientSelection';

const API_KEY_LENGTH = 40;

const allowedReportTypesForClientRoles = [2, 3, 4, 5, 6, 8, 9, 11, 12, 13, 14, 15];

export default function UserInviteEdit(props) {
    const [firstName, setFirstName] = useState(null);
    const [lastName, setLastName] = useState(null);
    const [emailAddress, setEmailAddress] = useState("");
    const [userRole, setUserRole] = useState(null);
    const [canSave, setCanSave] = useState(false);
    const [clients, setClients] = useState(null);
    const [siteIds, setSiteIds] = useState([]);
    const [siteGroups, setSiteGroups] = useState([]);
    const [selectedClients, setSelectedClients] = useState([])
    const [allowCancellationOfBreaches, setAllowCancellationOfBreaches] = useState(false)    
    const [maximumCancellationsPerMonth, setMaximumCancellationsPerMonth] = useState("")
    const [maximumNumberOfAllowListEntries, setMaximumNumberOfAllowListEntries] = useState("")
    const [clientsAllowedVehicleAnalytics, setClientsAllowedVehicleAnalytics] = useState(false)
    const [wardenNumber, setWardenNumber] = useState("")
    const [maxAllowListEnabled, setMaxAllowListEnabled] = useState(false);
    const [reportIds, setReportIds] = useState([]);
    const [allowAccessToReports, setAllowAccessToReports] = useState(false);
    const [allowAccessToPayments, setAllowAccessToPayments] = useState(false);
    const [lastLogin, setLastLogin] = useState(null)
    const [orgAllowsApiAccess, setOrgAllowsApiAccess] = useState(false)
    const [userAllowsApiAccess, setUserAllowsApiAccess] = useState(false)
    const [apiKeyLastFour, setApiKeyLastFour] = useState(null)
    const [revealedApiKey, setRevealedApiKey] = useState(null)
    const [errorMessage, setErrorMessage] = useState(null)
    const history = useHistory();
    const [isSelectAllReportsChecked, setIsSelectAllReportsChecked] = useState(false)
    const { editUserId } = useParams()
    const userManagementUrl = `/organisation/${props.organisationId}/user-management`

    const userRolesInHierarchicalOrder = [0, 1, 2, 9, 8, 4, 5, 6].map(
        roleKey => ([ roleKey, Enums.UserRoles[roleKey] ])
    )

    const submit = function () {
        if (canSave) {
            if (props.apiUserMode) {
                Api.addApiUser(
                    data => {
                        if (data.isError) {
                            setErrorMessage(data.errorMessage)
                        } else {
                            setRevealedApiKey(data.apiKey)
                        }
                    },
                    error => {
                        console.log(error)
                        setErrorMessage(error.errorMessage)
                    },
                    firstName, lastName, emailAddress, userRole, selectedClients, siteIds, siteGroups,
                    props.organisationId,
                    props.token
                )
            } else if (!props.editMode) {
                Api.addInvitation(
                data => {
                    if (data.isError) {
                        setErrorMessage(data.errorMessage)
                    } else {
                        history.push(userManagementUrl)
                    }
                },
                error => {
                    console.log(error)
                    setErrorMessage(error.errorMessage)
                },
                emailAddress, userRole, selectedClients, siteIds, siteGroups, wardenNumber,
                props.organisationId,
                props.token
                )
            } else {
                Api.editUserRole(
                    data => {
                        if (data.isError) {
                            setErrorMessage(data.errorMessage)
                        } else {
                            history.push(userManagementUrl)
                        }
                    },
                    error => {
                        console.log(error)
                        setErrorMessage(error.errorMessage)
                    },
                    firstName, lastName, editUserId, userRole, !arrayUtilities.isNullOrEmpty(selectedClients) ? selectedClients : [], siteIds, siteGroups, stringHelpers.isNullOrEmpty(maximumCancellationsPerMonth) ? null : Number(maximumCancellationsPerMonth),
                    maxAllowListEnabled ? Number(maximumNumberOfAllowListEntries) : null,
                    allowAccessToReports && !arrayUtilities.isNullOrEmpty(reportIds) ? reportIds.map(item => Number(item)) : null,
                    allowAccessToPayments,
                    userAllowsApiAccess,
                    clientsAllowedVehicleAnalytics,
                    wardenNumber,
                    props.organisationId,
                    props.token
                )
            }
        }
    }

    useEffect(() => {
        setCanSave(
            userRole !== null &&
            (
                props.editMode ||
                (props.apiUserMode && !stringHelpers.isNullOrEmpty(firstName) && !stringHelpers.isNullOrEmpty(lastName) && !stringHelpers.isNullOrEmpty(emailAddress) && stringHelpers.isValidEmailFormat(emailAddress)) ||
                (!props.editMode && !props.apiUserMode && !stringHelpers.isNullOrEmpty(emailAddress) && stringHelpers.isValidEmailFormat(emailAddress))
            )
        )
    }, [emailAddress, userRole, firstName, lastName])

    useEffect(() => {
        Api.getClients((data) => {
            setClients(data);
            if (props.editMode) {
                Api.getUserById(
                    data => {
                        setFirstName(data.firstName);
                        setLastName(data.lastName);

                        const org = data.organisations.find(item => item.organisation.id === props.organisationId)
                        if (org !== undefined) {
                            setUserRole(org.roleId)
                            setSiteIds(org.siteIds)
                            setSiteGroups(org.siteGroupIds)
                            setSelectedClients(!arrayUtilities.isNullOrEmpty(org.clientIds) ? org.clientIds : [])
                            setAllowCancellationOfBreaches(org.maximumCancellationsPerMonth && org.maximumCancellationsPerMonth !== null && org.maximumCancellationsPerMonth > 0);
                            setMaximumCancellationsPerMonth(org.maximumCancellationsPerMonth === null ? "" : String(org.maximumCancellationsPerMonth))
                            if (org.maximumNumberOfAllowListEntries !== null) {
                                setMaximumNumberOfAllowListEntries(String(org.maximumNumberOfAllowListEntries))
                                setMaxAllowListEnabled(true)
                            }
                            if (!arrayUtilities.isNullOrEmpty(org.reportIds)) {
                                setAllowAccessToReports(true)
                                setReportIds(org.reportIds.map(item => String(item)))
                                if (org.reportIds.length === allowedReportTypesForClientRoles.length) {
                                    setIsSelectAllReportsChecked(true)
                                }
                            } else {
                                setAllowAccessToReports(false)
                                setReportIds([])
                                setIsSelectAllReportsChecked(false)
                            }
                            setAllowAccessToPayments(org.payments)
                            setClientsAllowedVehicleAnalytics(org.vehicleAnalytics)
                            setWardenNumber(org.wardenNumber ?? "")
                            
                        }
                        setLastLogin(data.lastLogin)
                        setUserAllowsApiAccess(data.allowApiAccess)
                        setApiKeyLastFour(data.apiKeyLastFour)
                    },
                    error => console.log(error.errorMessage),
                    editUserId,
                    props.token
                )
                
            }
        }, () => { }, props.organisationId, props.token);
    }, [])

    useEffect(() => {
        Api.getOrganisation((data) => {
            setOrgAllowsApiAccess(data.allowApiAccess)
        }, () => setOrgAllowsApiAccess(false), props.organisationId, props.token)
    }, [])

    const toggleUserApiAccess = function () {
        if (userAllowsApiAccess) {
            setRevealedApiKey(null)
        }
        setUserAllowsApiAccess(!userAllowsApiAccess)
    }

    const rotateApiKey = function () {
        Api.rotateUserApiKey(
            data => {
                setRevealedApiKey(data.apiKey)
                setApiKeyLastFour(data.apiKey.slice(-4))
            },
            error => setErrorMessage(error.errorMessage),
            editUserId,
            props.token
        )
    }

    const isClientOrClientManagerSelected = userRole === 4 || userRole === 5
    const isWardenSelected = userRole === 6
    const allowedReportEntries = Object.entries(Enums.ReportType).filter(([key]) => allowedReportTypesForClientRoles.includes(Number(key)))

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="card mb-3">
                        <div className="card-header">
                            <div className="row align-items-center justify-content-between">
                                <div className="col-2 col-sm-auto d-flex align-items-center pr-0">
                                    <h5 className="mb-0">{props.editMode && firstName && lastName ? <>{firstName} {lastName}</> : props.apiUserMode ? <>Add API User</> : <>Invite User</>}</h5>
                                </div>
                            </div>
                        </div>
                        <div className="card-body bg-light border-top">
                            {revealedApiKey && props.apiUserMode ? <>
                                <div className="row">
                                    <div className="col-12">
                                        <div className="alert alert-warning mt-2">
                                            <strong>Copy this key now — it won't be shown again:</strong>
                                            <div className="mt-1" style={{ wordBreak: 'break-all' }}><code>{revealedApiKey}</code></div>
                                        </div>
                                    </div>
                                </div>
                            </> : <>
                            {props.apiUserMode && <>
                                <div className="row">
                                    <div className="col-md-3 col-12 ">
                                        <p className="font-weight-semi-bold mt-1 mt-2">First Name<span className="req"></span></p>
                                    </div>
                                    <div className="col-md-6 col-12">
                                        <div className="form-group mb-1">
                                            <input required className="w-100 border rounded py-2 px-3" type="text" autoFocus={true} value={firstName || ""} onChange={(e) => setFirstName(e.target.value)} />
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-3 col-12 ">
                                        <p className="font-weight-semi-bold mt-1 mt-2">Last Name<span className="req"></span></p>
                                    </div>
                                    <div className="col-md-6 col-12">
                                        <div className="form-group mb-1">
                                            <input required className="w-100 border rounded py-2 px-3" type="text" value={lastName || ""} onChange={(e) => setLastName(e.target.value)} />
                                        </div>
                                    </div>
                                </div>
                            </>}
                            {!props.editMode &&
                            <div className="row">
                                <div className="col-md-3 col-12 ">
                                    <p className="font-weight-semi-bold mt-1 mt-2">Email Address<span className="req"></span></p>
                                </div>
                                <div className="col-md-6 col-12">
                                    <div className="form-group mb-1">
                                        <input required className="w-100 border rounded py-2 px-3" type="text" autoFocus={ !props.apiUserMode } value={ emailAddress } onChange={ (e) => {
                                            setEmailAddress(e.target.value)
                                            formUtilities.requiredField(e.target.parentNode, e.target.value)
                                        } } />
                                        <small className='invalid-feedback text-danger'>Please enter an email</small>
                                    </div>
                                </div>
                            </div>}
                            {props.editMode && <div className="row">
                                <div className="col-md-3 col-12 ">
                                    <p className="font-weight-semi-bold">Last Login</p>
                                </div>
                                <div className="col-md-6 col-12">
                                    <div className="form-group">
                                        <p className='m-0'>{ toDateTimeString(lastLogin) }</p>
                                    </div>
                                </div>
                            </div>}
                            {props.editMode && orgAllowsApiAccess && <>
                                <div className="row mt-3 mb-1">
                                    <div className="col-md-3 col-12 ">
                                        <p className="font-weight-semi-bold mb-1">Allow API Access</p>
                                    </div>
                                    <div className="col-md-6 col-12 custom-control custom-switch">
                                        <input className="custom-control-input" type="checkbox" id="allow-user-api-access" checked={userAllowsApiAccess} onChange={() => toggleUserApiAccess()} />
                                        <label className="custom-control-label custom-toggle ml-3" htmlFor="allow-user-api-access"></label>
                                        {userAllowsApiAccess && <>Yes</>}{!userAllowsApiAccess && <>No</>}
                                    </div>
                                </div>
                                {userAllowsApiAccess && <div className="row mt-3 mb-1">
                                    <div className="col-md-3 col-12 ">
                                        <p className="font-weight-semi-bold mb-1">API Key</p>
                                    </div>
                                    <div className="col-md-6 col-12">
                                        <div className="input-group mb-0">
                                            <input readOnly className="border rounded-left form-control mb-1 form-control-plaintext px-3 bg-white" type="text" value={apiKeyLastFour ? `${'•'.repeat(API_KEY_LENGTH - apiKeyLastFour.length)}${apiKeyLastFour}` : 'No key generated'} />
                                            <div className="input-group-append">
                                                <button type="button" data-toggle="modal" data-target="#rotate-api-key" id="rotate-api-key-modal" className="btn btn-falcon-default btn-sm py-3 d-flex align-items-center">
                                                    <i className={apiKeyLastFour ? 'fas fa-undo' : 'fas fa-key'}></i>&nbsp;{apiKeyLastFour ? 'Rotate Key' : 'Generate Key'}
                                                </button>
                                            </div>
                                        </div>
                                        {revealedApiKey && <div className="alert alert-warning mt-2">
                                            <strong>Copy this key now — it won't be shown again:</strong>
                                            <div className="mt-1" style={{ wordBreak: 'break-all' }}><code>{revealedApiKey}</code></div>
                                        </div>}
                                        <div className="modal fade" id="rotate-api-key" data-parent="#rotate-api-key-modal" tabIndex="-1" role="dialog">
                                            <div className="modal-dialog" role="document">
                                                <div className="modal-content">
                                                    <div className="modal-header">
                                                        <h5 className="modal-title">Warning</h5>
                                                        <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                                                            <span aria-hidden="true">&times;</span>
                                                        </button>
                                                    </div>
                                                    <div className="modal-body">
                                                        This will generate a new API key for this user. Any existing key will stop working immediately. Are you sure?
                                                    </div>
                                                    <div className="modal-footer">
                                                        <button className="btn btn-sm btn-falcon-default" data-dismiss="modal">Close</button>
                                                        <button onClick={() => rotateApiKey()} className="btn btn-sm btn-success" data-dismiss="modal">Confirm</button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>}
                            </>}
                            <div className="row">
                                <div className="col-md-3 col-12 ">
                                    <p className="font-weight-semi-bold">User Role<span className="req"></span></p>
                                </div>
                                <div className="col-md-6 col-12 ">
                                    <div className="form-group mb-1">
                                        {
                                            userRolesInHierarchicalOrder
                                            .map(([key, value]) =><>
                                                {
                                                    <div className='mb-3'>
                                                    <div className='d-flex'>
                                                        <input checked={ userRole === key } id={ key } onChange={ () => {
                                                            setUserRole(key)
                                                        } } className='mr-2' name="role-selection-radio" key={ key } value={ key } type='radio' />
                                                        <label className='m-0' htmlFor={ key }>{ value }</label>
                                                    </div>
                                                    <small>{ userRoleDescriptions[key] }</small>
                                                </div>}</>
                                            )
                                        }
                                        <small className='invalid-feedback text-danger'>Please select a role</small>
                                    </div>
                                </div>
                            </div>
                            {
                                (isClientOrClientManagerSelected || isWardenSelected) &&
                                <>
                                    {
                                        
                                        <div className="row">
                                            <div className="col-md-3 col-12 ">
                                                <p className="font-weight-semi-bold">Client</p>
                                            </div>
                                            <div className="col-md-6 col-12 ">
                                                    {
                                                        !arrayUtilities.isNullOrEmpty(clients) && clients
                                                            .map(
                                                                client => <ClientSelection 
                                                                    key={client.id}
                                                                    client={client}
                                                                    onClientCheckboxChange={ event => setSelectedClients(arrayUtilities.toggle(client.id, selectedClients))}
                                                                    isClientChecked={selectedClients.includes(client.id)}
                                                                    removeMultipleSiteGroups={ arrayOfIds => setSiteGroups(prevState => prevState.filter(item => !arrayOfIds.includes(item))) }
                                                                    removeMultipleSites={ arrayOfIds => setSiteIds(prevState => prevState.filter(item => !arrayOfIds.includes(item))) }
                                                                    userSiteGroups={ siteGroups }
                                                                    toggleSiteId={ id => setSiteIds(arrayUtilities.toggle(id, siteIds)) }
                                                                    toggleSiteGroup={ id => setSiteGroups(arrayUtilities.toggle(id, siteGroups)) }
                                                                    userAssignedSites={siteIds}
                                                                    organisationId={props.organisationId}
                                                                    token={props.token}
                                                                />
                                                            )
                                                    }
                                            </div>
                                        </div>
                                    }
                                </>
                            }
                            {
                                !props.apiUserMode && isWardenSelected &&
                                <div className="row">
                                    <div className="col-md-3 col-12 ">
                                        <p className="font-weight-semi-bold mt-2">Warden number</p>
                                    </div>
                                    <div className="col-md-6 col-12 ">
                                        <div className="form-group mb-1">
                                            <input className="w-100 border rounded py-2 px-3" type="text" maxLength={ 50 } value={ wardenNumber } onChange={ e => setWardenNumber(e.target.value) } />
                                        </div>
                                    </div>
                                </div>
                            }
                            {
                                props.editMode && isClientOrClientManagerSelected &&
                                <>
                                    <div className="row mt-3 mb-1">
                                        <div className="col-md-3 col-12 ">
                                            <p className="font-weight-semi-bold mb-1">Allow access to reports</p>
                                        </div>
                                        <div className="col-md-6 col-12 custom-control custom-switch">
                                            <input className="custom-control-input" type="checkbox" id="allow-access-to-reports" checked={allowAccessToReports} onChange={(e) => {
                                                setAllowAccessToReports(e.target.checked)
                                            }}/>
                                            <label className="custom-control-label custom-toggle ml-3" htmlFor="allow-access-to-reports"></label>
                                            {allowAccessToReports && <>Yes</>}{!allowAccessToReports && <>No</>}
                                        </div>
                                    </div>
                                    {allowAccessToReports && <div className="row mt-3 mb-1">
                                        <div className="col-md-3 col-12 ">
                                            <p className="font-weight-semi-bold mb-1">Select reports</p>
                                        </div>
                                        <div className="col-md-4">
                                            <table className="table table-sm mb-0 table-striped table-dashboard fs--1 data-table w-100 border-bottom border-200 no-footer">
                                                <thead className="bg-200 text-900">
                                                <tr>
                                                    <td>
                                                        {

                                                            <input
                                                                type="checkbox"
                                                                checked={isSelectAllReportsChecked}
                                                                id="select-all-sites"
                                                                onChange={e => arrayUtilities
                                                                    .handleSelectAll(e,isSelectAllReportsChecked,setIsSelectAllReportsChecked,() => setReportIds([]),() => setReportIds(allowedReportTypesForClientRoles.map(key => String(key))))}
                                                            />}
                                                    </td>
                                                    <th>Report</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {allowedReportEntries
                                                    .map(([key,value]) => (<tr key={key}>
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                checked={reportIds.includes(key)}
                                                                id={`select-report-type-${key}`}
                                                                onChange={e => arrayUtilities
                                                                    .handleListSelection(e.target.checked,{ id:key },setReportIds,reportIds,allowedReportEntries,isSelectAllReportsChecked,setIsSelectAllReportsChecked)}
                                                            />
                                                        </td>
                                                        <td>{value}</td>
                                                    </tr>))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>}
                                </>

                            }
                            {props.editMode && isClientOrClientManagerSelected && <div className="row mt-3 mb-1">
                                <div className="col-md-3 col-12 ">
                                    <p className="font-weight-semi-bold mb-1">Allow access to payments</p>
                                </div>
                                <div className="col-md-6 col-12 custom-control custom-switch">
                                    <input className="custom-control-input" type="checkbox" id="allow-access-to-payments" checked={allowAccessToPayments} onChange={(e) => {
                                        setAllowAccessToPayments(e.target.checked)
                                    }}/>
                                    <label className="custom-control-label custom-toggle ml-3" htmlFor="allow-access-to-payments"></label>
                                    {allowAccessToPayments && <>Yes</>}{!allowAccessToPayments && <>No</>}
                                </div>
                            </div>}
                                {
                                    props.editMode && isClientOrClientManagerSelected &&
                                    <div className="row">
                                        <div className="col-md-3 col-12 ">
                                            <p className="font-weight-semi-bold mb-1">Allow access to vehicle analytics</p>
                                        </div>
                                        <div className="col-md-6 col-12 custom-control custom-switch">
                                            <input className="custom-control-input" type="checkbox" id="client-vehicle-analytics-enabling-switch" checked={ clientsAllowedVehicleAnalytics } onChange={ () => setClientsAllowedVehicleAnalytics(!clientsAllowedVehicleAnalytics) } />
                                            <label className="custom-control-label custom-toggle ml-3" htmlFor="client-vehicle-analytics-enabling-switch"></label>
                                        {clientsAllowedVehicleAnalytics && <>Yes</>}{!clientsAllowedVehicleAnalytics && <>No</>}
                                        </div>
                                    </div>
                                }
                            {props.editMode && isClientOrClientManagerSelected && <>
                                <div className="row">
                                    <div className="col-md-3 col-12 ">
                                        <p className="font-weight-semi-bold mt-2">Allowing cancelling of breaches</p>
                                    </div>
                                    <div className="col-md-6 col-12 custom-control custom-switch mt-2">
                                        <input className="custom-control-input" type="checkbox" id="allow-cancellation-of-breaches" checked={allowCancellationOfBreaches} onChange={(e) => {
                                            if ( !e.target.checked ) {
                                                setMaximumCancellationsPerMonth("")
                                            }
                                            setAllowCancellationOfBreaches(e.target.checked)
                                        }}/>
                                        <label className="custom-control-label custom-toggle ml-3" htmlFor="allow-cancellation-of-breaches"></label>
                                        {allowCancellationOfBreaches && <>Yes</>}{!allowCancellationOfBreaches && <>No</>}
                                    </div>
                                </div>
                                {allowCancellationOfBreaches && <div className="row">
                                    <div className="col-md-3 col-12 ">
                                        <p className="font-weight-semi-bold mt-2">Maximum cancellations per month</p>
                                    </div>
                                    <div className="col-md-6 col-12 ">
                                        <div className="form-group mb-1">
                                            <input required className="w-100 border rounded py-2 px-3" type="number" value={maximumCancellationsPerMonth} onChange={e => setMaximumCancellationsPerMonth(e.target.value)}/>
                                        </div>
                                    </div>
                                </div>}
                                <div className="row mt-2 mb-3">
                                    <div className="col-md-3 col-12 ">
                                        <p className="font-weight-semi-bold mb-1">Limit Allow List Entries</p>
                                    </div>
                                    <div className="col-md-6 col-12 custom-control custom-switch">
                                        <input className="custom-control-input" type="checkbox" id="max-allow-list-enabling-switch" checked={maxAllowListEnabled} onChange={() => setMaxAllowListEnabled(!maxAllowListEnabled)}/>
                                        <label className="custom-control-label ml-3" htmlFor="max-allow-list-enabling-switch"></label>
                                    </div>
                                </div>
                                {maxAllowListEnabled && <div className="row">
                                    <div className="col-md-3 col-12 ">
                                        <p className="font-weight-semi-bold mt-2">Maximum allow list entries</p>
                                    </div>
                                    <div className="col-md-6 col-12 ">
                                        <div className="form-group mb-1">
                                            <input required className="w-100 border rounded py-2 px-3" type="number" value={ maximumNumberOfAllowListEntries } onChange={ e => setMaximumNumberOfAllowListEntries(e.target.value) } />
                                            </div>
                                        </div>
                                    </div>}
                                </>
                            }
                            </>}
                        </div>
                        <div className="card-footer border-top ">
                            <div className="row client-edit-buttons">
                                <div className="col-12 col-sm-auto ml-auto  pl-0">
                                    <div id="dashboard-actions">
                                        {revealedApiKey && props.apiUserMode ? <>
                                            <Link className="btn btn-success btn-sm ml-2" to={ userManagementUrl }><i className="fas fa-check"></i> Done</Link>
                                        </> : <>
                                            <Link className="btn btn-falcon-default btn-sm" to={ userManagementUrl }><i className="fas fa-times"></i> Cancel</Link>
                                            <button type="button" className="btn btn-success btn-sm ml-2" disabled={ !canSave } onClick={ () => submit() }>
                                                <i className="fas fa-check"></i> Confirm</button>
                                        </>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {
                !stringHelpers.isNullOrEmpty(errorMessage)
                && <ErrorModal message={errorMessage} onHide={() => setErrorMessage(null)} />
            }
        </div>
    )
}